import { createId } from '@paralleldrive/cuid2'
import type { Team } from '@rhythmic/db/types'
import type { CreateTeamPayload } from '@rhythmic/types'
import { CreateTeamSchema, TeamSchema } from '@rhythmic/types'
import type { Transaction } from '@tanstack/vue-db'
import { useRhythmicLogger } from '#imports'

import { getTeamCollection } from '../../getCollections'

const logger = useRhythmicLogger('useTeamContext:createNewTeam')

export interface CreateNewTeamContext {
  workspaceId: string
  workspaceSlug: string
  teams: Team[]
}

function validateParams(payload: CreateTeamPayload, context: CreateNewTeamContext) {
  const { name, slug, avatar_url } = CreateTeamSchema.parse(payload)
  const { workspaceId, workspaceSlug, teams } = context

  if (!workspaceId) {
    const error = new Error('Workspace context is not ready')
    throw error
  }
  if (!workspaceSlug) {
    const error = new Error('Workspace context is not ready')
    throw error
  }

  const duplicate = teams.some((existing) => existing.slug === slug)
  if (duplicate) {
    const error = new Error('A team with this slug already exists in the workspace')
    logger.warn('Unable to create team: duplicate slug detected', { slug })
    throw error
  }

  return {
    name,
    slug,
    avatar_url,
    workspaceId,
    workspaceSlug,
  }
}

export default function handleCreateNewTeam(
  payload: CreateTeamPayload,
  context: CreateNewTeamContext,
): Transaction<Team> {
  try {
    const { name, slug, avatar_url, workspaceId, workspaceSlug } = validateParams(payload, context)

    const teamCollection = getTeamCollection(workspaceSlug)

    const newTeam = TeamSchema.parse({
      id: `team_${createId()}`,
      created_at: new Date(),
      updated_at: new Date(),
      workspace_id: workspaceId,
      workspace_slug: workspaceSlug,
      slug,
      name,
      avatar_url: avatar_url ?? null,
      context: payload.context ?? null,
    })

    const transaction = teamCollection.insert(newTeam) as Transaction<Team>
    return transaction
  } catch (error) {
    logger.error('createNewTeam failed', {
      error,
    })
    if (error instanceof Error) throw error
    throw new Error('Failed to create team')
  }
}
